import { MEMORY_SCOPES, type MemoryScope } from '@/lib/api/memory.js';

/**
 * The section's five views, in the order the prototype puts them in, with the agent's own
 * auto-saved facts last (MEDA-1153). `map` is the default.
 */
export const MEMORY_TABS = [
  'map',
  'document',
  'suggestions',
  'history',
  'auto-saved',
] as const;

/** One of the section's five views (see {@link MEMORY_TABS}). */
export type MemoryTab = (typeof MEMORY_TABS)[number];

/**
 * The two ways the Memory tab shows one document: as cards, one per memory, or as the Markdown the
 * agent actually reads. `cards` first, because it is the default.
 */
export const MEMORY_VIEWS = ['cards', 'source'] as const;

/** How the Memory tab is showing the document (see {@link MEMORY_VIEWS}). */
export type MemoryView = (typeof MEMORY_VIEWS)[number];

/** What `/memory` keeps in the address bar, so a view survives a reload and a shared link. */
export interface MemorySearch {
  tab?: MemoryTab;
  scope?: MemoryScope;
  /**
   * Which view of the Memory tab is open. Its own key rather than a fifth tab: the two are views of
   * one document, they share the scope switcher and the character budget, and a link naming a memory
   * has to be able to say "the cards" without naming a different tab.
   */
  view?: MemoryView;
  /**
   * One memory to open on, by block id. Written by the global search when somebody picks a memory out
   * of it, so the section opens on the entry they were looking for rather than on whatever came first.
   */
  block?: string;
}

/**
 * Validate `?tab=`, `?view=` and `?scope=` off a raw URL.
 *
 * Anything unrecognised becomes `undefined`, which the page reads as "use the default". A junk tab in
 * a stale bookmark must land on the section, not on a blank panel or a router error — the page is
 * still perfectly usable, it just opens where it always does.
 */
export function parseMemorySearch(raw: Record<string, unknown>): MemorySearch {
  return {
    tab: (MEMORY_TABS as readonly unknown[]).includes(raw.tab)
      ? (raw.tab as MemoryTab)
      : undefined,
    view: (MEMORY_VIEWS as readonly unknown[]).includes(raw.view)
      ? (raw.view as MemoryView)
      : undefined,
    scope: (MEMORY_SCOPES as readonly unknown[]).includes(raw.scope)
      ? (raw.scope as MemoryScope)
      : undefined,
    block:
      typeof raw.block === 'string' && raw.block !== '' ? raw.block : undefined,
  };
}

/**
 * Which view opens when the address names none.
 *
 * The map on a full-size screen, and the document on a narrow one — canon §16.6: a force-directed
 * graph on a phone is unreadable, so the mobile default is the text and the map is a deliberate tap.
 * `matchMedia` is absent in some runtimes (jsdom included), and its absence means "not a phone".
 */
export function defaultMemoryTab(): MemoryTab {
  if (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
  ) {
    return 'map';
  }
  return window.matchMedia('(max-width: 767px)').matches ? 'document' : 'map';
}

/**
 * Which view of the Memory tab opens when the address names none.
 *
 * The cards. They are the memories themselves — one entry, one card, each with its own version and its
 * own conflict — while the Markdown is the projection the agent reads. Somebody who came to change one
 * thing should not have to find it in a 16,000-character box first.
 */
export function defaultMemoryView(): MemoryView {
  return 'cards';
}
